import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Gamepad2, Zap, Cable, Activity, Radio, Gift, CheckCircle2,
  Trophy, Users, CalendarDays, Mic2, Sparkles, ArrowRight, ArrowLeft,
  Wifi, ShieldAlert,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_silicom-pro-futbol/artifacts/bsulykak_Untitled%20Project%20-%20Artboard%201%20%281%29%20%281%29%20%281%29.png";

const HERO_VIDEOS = [
  {
    src: "https://assets.mixkit.co/videos/51614/51614-720.mp4",
    poster: "https://assets.mixkit.co/videos/51614/51614-thumb-720-0.jpg",
    label: "Streaming y creación de contenido",
  },
  {
    src: "https://assets.mixkit.co/videos/43541/43541-720.mp4",
    poster: "https://assets.mixkit.co/videos/43541/43541-thumb-720-0.jpg",
    label: "Competencia online y comunidad",
  },
  {
    src: "https://assets.mixkit.co/videos/51620/51620-720.mp4",
    poster: "https://assets.mixkit.co/videos/51620/51620-thumb-720-0.jpg",
    label: "Setup PC gamer en casa",
  },
];

const SESSION_KEY = "silicom-gaming-sid";
const SUBMIT_KEY = "silicom-gaming-submitted";
const FORM_DRAFT_KEY = "silicom-gaming-draft";
const UTM_KEY = "silicom-gaming-utm";

const getSessionId = () => {
  let v = sessionStorage.getItem(SESSION_KEY);
  if (!v) {
    v = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, v);
  }
  return v;
};

const parseAndStoreUtms = () => {
  const p = new URLSearchParams(window.location.search);
  const current = {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
    referrer: document.referrer || null,
  };
  // persist on first visit so attribution survives form steps
  const existing = JSON.parse(sessionStorage.getItem(UTM_KEY) || "null");
  if (!existing && (current.utm_source || current.utm_medium || current.utm_campaign)) {
    sessionStorage.setItem(UTM_KEY, JSON.stringify(current));
    return current;
  }
  return existing || current;
};

const getUtms = () => JSON.parse(sessionStorage.getItem(UTM_KEY) || "null") || parseAndStoreUtms();

const track = (event, meta = null) => {
  const utms = getUtms();
  axios.post(`${API}/gaming/events`, {
    event,
    session_id: getSessionId(),
    utm_source: utms?.utm_source || null,
    utm_medium: utms?.utm_medium || null,
    utm_campaign: utms?.utm_campaign || null,
    referrer: utms?.referrer || null,
    meta,
  }).catch(() => {});
};

const Hero = ({ onCta }) => {
  const [active, setActive] = useState(0);
  const videoRefs = useRef([]);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    if (prefersReducedMotion.current) return;
    const id = setInterval(() => setActive((c) => (c + 1) % HERO_VIDEOS.length), 6500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === active) video.play().catch(() => {});
      else video.pause();
    });
  }, [active]);

  return (
    <section className="relative min-h-[92vh] overflow-hidden bg-slate-950 text-white">
      <div className="absolute inset-0" aria-hidden="true">
        {HERO_VIDEOS.map((video, index) => (
          <video
            key={video.src}
            ref={(el) => (videoRefs.current[index] = el)}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
              index === active ? "opacity-60" : "opacity-0"
            }`}
            src={video.src}
            poster={video.poster}
            loop
            muted
            playsInline
            preload={index === active ? "auto" : "none"}
          />
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-[#081527]/72 to-[#260d2b]/55" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950 to-transparent" />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-24 h-96 w-96 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute bottom-24 right-0 h-96 w-96 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/50 to-transparent" />
      </div>

      <div className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <a
            href="/"
            data-testid="gaming-logo"
            className="inline-flex items-center rounded-full bg-white/95 px-4 py-2 shadow-lg shadow-black/20 backdrop-blur transition-transform hover:scale-[1.02]"
            aria-label="Volver al sitio de Silicomlan"
          >
            <img src={LOGO_URL} alt="Silicomlan" className="h-10 w-auto md:h-12" />
          </a>
          <div className="hidden items-center gap-3 md:flex">
            <span className="rounded-full border border-sky-300/25 bg-sky-400/10 px-4 py-2 text-xs font-black uppercase tracking-[0.25em] text-sky-200 backdrop-blur">
              Vertical Gaming
            </span>
            <a
              href="/"
              data-testid="gaming-back-home"
              className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white/90 backdrop-blur transition-colors hover:bg-white/20"
            >
              Volver al sitio
            </a>
          </div>
        </div>
      </div>

      <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-center px-6 pb-24 pt-32 md:pt-36">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-500/10 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.3em] text-cyan-200 backdrop-blur">
            <Gamepad2 className="h-3.5 w-3.5" /> Silicomlan Gaming
          </div>
          <h1 className="mt-6 text-4xl font-black leading-[1.05] tracking-tight md:text-7xl">
            Buscamos <span className="bg-gradient-to-r from-cyan-300 to-sky-300 bg-clip-text text-transparent">gamers</span> para formar el <span className="bg-gradient-to-r from-sky-300 via-cyan-200 to-white bg-clip-text text-transparent">equipo de Silicomlan</span>
            <span className="mt-4 block text-3xl text-cyan-300 md:text-5xl">¿Te sumás?</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
            Jugás en PC, consola o celular: queremos conocerte. Contanos qué jugás y sumate desde el comienzo.
          </p>
          <p className="mt-4 text-sm font-bold uppercase tracking-[0.3em] text-sky-300">
            La red también se juega.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {["FTTH", "Baja latencia", "Red propia"].map((item) => (
              <span key={item} className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white/85 backdrop-blur">
                {item}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              data-testid="gaming-hero-cta"
              onClick={onCta}
              className="group rounded-full bg-gradient-to-r from-cyan-500 to-sky-500 px-7 py-7 text-base font-black uppercase tracking-wider text-white shadow-[0_0_40px_rgba(236,72,153,0.35)] hover:from-cyan-600 hover:to-sky-600"
            >
              Quiero ser parte
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <a
              href="#beneficios"
              data-testid="gaming-hero-benefits"
              className="rounded-full border border-white/20 bg-white/10 px-6 py-4 text-sm font-bold uppercase tracking-widest text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              Ver por qué importa la red
            </a>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-slate-300">
            <Wifi className="h-4 w-4 text-sky-300" />
            <span>{HERO_VIDEOS[active].label}</span>
          </div>
          <div className="flex items-center gap-2">
            {HERO_VIDEOS.map((video, index) => (
              <button
                key={video.src}
                type="button"
                aria-label={`Ver escena ${index + 1}`}
                data-testid={`gaming-video-dot-${index}`}
                onClick={() => setActive(index)}
                className={`h-2.5 rounded-full transition-all ${
                  index === active ? "w-8 bg-cyan-400" : "w-2.5 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Beneficios = () => {
  const items = [
    { icon: Cable, title: "FTTH", desc: "Fibra óptica hasta tu hogar." },
    { icon: Activity, title: "Baja latencia", desc: "En gaming online cada milisegundo importa." },
    { icon: Zap, title: "Estabilidad", desc: "Una conexión estable es tan importante como la velocidad." },
    { icon: Radio, title: "Streaming", desc: "Para quienes transmiten, crean contenido o juegan en vivo." },
  ];
  return (
    <section id="beneficios" className="relative bg-slate-950 py-20 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute left-1/4 top-10 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-6xl px-6">
        <div className="mb-14 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-300">Gaming no es solo "más megas"</p>
          <h2 className="mt-3 text-3xl font-black md:text-5xl">Una conexión a la altura de tu partida</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur transition-colors hover:border-cyan-300/40">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-sky-400/20 to-cyan-500/20 text-sky-200">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-xl font-bold">{title}</h3>
              <p className="mt-2 text-sm text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-3xl border border-sky-300/25 bg-gradient-to-r from-sky-500/10 via-white/5 to-cyan-500/10 p-7 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cyan-200">Red propia Silicomlan</p>
          <p className="mt-3 text-slate-200 md:text-lg">
            Operamos infraestructura propia de fibra óptica. La calidad de una conexión no se mide únicamente en Mbps.
          </p>
        </div>
      </div>
    </section>
  );
};

const Posicionamiento = () => (
  <section className="relative overflow-hidden bg-gradient-to-b from-white via-sky-50 to-cyan-50 py-24 text-slate-900">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute left-0 top-10 h-72 w-72 rounded-full bg-sky-200/50 blur-3xl" />
      <div className="absolute bottom-10 right-0 h-72 w-72 rounded-full bg-cyan-200/60 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-4xl px-6 text-center">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-cyan-500">Una vertical dentro de Silicomlan</p>
      <h2 className="mt-4 text-4xl font-black leading-tight tracking-tight md:text-6xl">
        No todos usamos internet{" "}
        <span className="bg-gradient-to-r from-sky-500 to-cyan-500 bg-clip-text text-transparent">de la misma manera.</span>
      </h2>
      <div className="mx-auto mt-10 max-w-2xl space-y-2 text-lg text-slate-600 md:text-xl">
        <p>Conectamos hogares.</p>
        <p>Conectamos empresas.</p>
        <p>Conectamos organismos.</p>
        <p className="pt-4 font-bold text-slate-900">Y también conectamos a quienes juegan, compiten, transmiten y crean.</p>
      </div>
      <p className="mt-10 inline-block rounded-full border border-cyan-200 bg-white px-5 py-2 text-sm font-black uppercase tracking-[0.3em] text-cyan-500 shadow-sm">
        Silicomlan Gaming
      </p>
    </div>
  </section>
);

/* ---------- Form Pill ---------- */
const slugify = (value) => String(value).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Pill = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid || `gaming-pill-${slugify(children)}`}
    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
      active
        ? "border-cyan-400 bg-cyan-100 text-cyan-700 shadow-sm shadow-cyan-100"
        : "border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:text-slate-900"
    }`}
  >
    {children}
  </button>
);

const PLATAFORMAS = ["PC", "PlayStation", "Xbox", "Nintendo", "Celular / Tablet", "Otra"];
const ACTIVIDADES = ["Discord u otra comunidad", "Equipo / clan", "Torneos", "Esports", "Streaming", "Creación de contenido", "Organizo torneos o comunidades", "Ninguna"];
const INTERESES = ["Torneos online", "Torneos presenciales", "Comunidad / Discord", "Eventos gamer", "Competencias entre equipos", "Streaming y contenidos", "Beneficios y premios", "Quiero conocer primero la propuesta"];
const EDADES = ["Menos de 13", "13 a 17", "18 a 24", "25 a 34", "35 a 44", "45 o más"];
const MENORES = new Set(["Menos de 13", "13 a 17"]);

const emptyForm = {
  quien_juega: "", edad_rango: "", plataformas: [],
  juegos: "", nivel: "", cantidad_jugadores: "",
  actividades: [], comunidad_detalle: "", intereses: [],
  nombre: "", whatsapp: "", es_cliente: "",
  consentimiento: false, autorizacion_tutor: false,
};

const StepHeader = ({ step, total, title, subtitle }) => (
  <div>
    <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.3em] text-cyan-500">
      <span data-testid="gaming-step-indicator">Paso {step} de {total}</span>
      <span className="text-slate-400">{Math.round((step / total) * 100)}%</span>
    </div>
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div
        data-testid="gaming-progress-bar"
        className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-sky-500 transition-all duration-500"
        style={{ width: `${(step / total) * 100}%` }}
      />
    </div>
    <h3 className="mt-5 text-2xl font-black text-slate-900 md:text-3xl">{title}</h3>
    {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
  </div>
);

const Encuesta = ({ onSuccess }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState(() => {
    try {
      const draft = JSON.parse(sessionStorage.getItem(FORM_DRAFT_KEY) || "null");
      if (draft) return { ...emptyForm, ...draft };
    } catch (e) { /* ignore */ }
    return emptyForm;
  });

  useEffect(() => {
    sessionStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(f));
  }, [f]);

  const bump = () => { if (!started) { setStarted(true); track("survey_start"); } };
  const set = (k, v) => { bump(); setF((s) => ({ ...s, [k]: v })); };
  const toggle = (k, v) => { bump(); setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] })); };

  const isMenor = useMemo(() => MENORES.has(f.edad_rango), [f.edad_rango]);

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!f.quien_juega) return "Elegí quién juega en tu casa";
      if (!f.edad_rango) return "Indicá la edad del gamer";
      if (!f.plataformas.length) return "Elegí al menos una plataforma";
    }
    if (currentStep === 2) {
      if (!f.nivel) return "Indicá cómo te considerás como jugador";
      if (!f.cantidad_jugadores) return "Elegí con cuántas personas jugás";
    }
    if (currentStep === 4) {
      if (!f.nombre.trim()) return "Ingresá tu nombre";
      const digits = (f.whatsapp || "").replace(/\D+/g, "");
      if (digits.length < 10) return "Ingresá un WhatsApp válido (10 dígitos)";
      if (!f.es_cliente) return "Indicá si sos cliente de Silicomlan";
      if (!f.consentimiento) return "Para participar necesitamos tu consentimiento";
      if (isMenor && !f.autorizacion_tutor) return "Al ser menor de 18 necesitamos la autorización de madre/padre/tutor";
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast({ title: "Faltan datos", description: err, variant: "destructive" }); return; }
    track(`survey_step_${step}_complete`);
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: document.getElementById("encuesta")?.offsetTop ?? 0, behavior: "smooth" });
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    const err = validateStep(4);
    if (err) { toast({ title: "Faltan datos", description: err, variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      const utms = getUtms();
      await axios.post(`${API}/gaming/survey`, {
        ...f,
        session_id: getSessionId(),
        utm_source: utms?.utm_source || null,
        utm_medium: utms?.utm_medium || null,
        utm_campaign: utms?.utm_campaign || null,
        referrer: utms?.referrer || null,
      });
      sessionStorage.setItem(SUBMIT_KEY, "1");
      sessionStorage.removeItem(FORM_DRAFT_KEY);
      onSuccess();
    } catch (err2) {
      toast({ title: "Error al enviar", description: err2?.response?.data?.detail || "Intentalo de nuevo", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="encuesta" className="relative bg-gradient-to-br from-cyan-50 via-white to-sky-50 py-20 text-slate-900">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-widest text-cyan-600 shadow-sm">
            <Gift className="h-3.5 w-3.5" /> Comunidad Silicomlan Gaming
          </div>
          <h2 className="mt-4 text-3xl font-black md:text-5xl">¿Hay un gamer en tu casa?</h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-600">
            Jugás en PC, consola o celular: queremos conocerte. No importa si sos vos, tu hijo, tu pareja u otro integrante de tu hogar. Si jugás, competís, hacés streaming o pertenecés a un equipo, clan o comunidad, queremos saber un poco más de vos.
          </p>
          <p className="mt-3 text-sm text-slate-500">Estamos empezando a construir la comunidad Silicomlan Gaming.</p>
        </div>

        <div className="mx-auto mt-8 max-w-xl overflow-hidden rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-sky-50 px-6 pb-6 pt-5 text-center shadow-md shadow-cyan-100" data-testid="giveaway-incentive">
          <p className="text-xs font-black uppercase tracking-widest text-cyan-700">🎮 Registrate y participá</p>
          <img
            src="/gigabyte-gs27fc2.png"
            alt="Monitor gamer curvo GIGABYTE GS27FC2"
            className="mx-auto mt-3 h-36 w-auto object-contain drop-shadow-[0_18px_25px_rgba(2,132,199,0.25)] md:h-44"
            loading="lazy"
            data-testid="giveaway-prize-image"
          />
          <p className="mt-3 text-base font-black leading-snug text-slate-900">
            Monitor gamer curvo <span className="text-sky-600">GIGABYTE GS27FC2</span>
            <span className="mt-1 block text-xs font-bold uppercase tracking-widest text-slate-500">27" · Full HD · 240 Hz</span>
          </p>
          <p className="mt-4 text-sm text-slate-600">
            Si en tu casa tienen Internet de Silicomlan, <b className="text-slate-900">¡tenés doble chance de ganar!</b>
          </p>
          <p className="mt-1.5 text-xs font-bold uppercase tracking-widest text-slate-500">Sorteo a fin de 2026</p>
        </div>

        <form onSubmit={submit} data-testid="gaming-form" className="mt-8 rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-xl shadow-cyan-100/70 backdrop-blur md:p-10">
          {/* STEP 1 */}
          {step === 1 && (
            <div data-testid="gaming-step-1">
              <StepHeader step={1} total={4} title="Tu perfil" subtitle="Contanos quién juega." />
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">¿Quién juega en tu casa?</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Yo", "Mi hijo/a", "Mi pareja", "Otro integrante", "Somos varios"].map(o => (
                    <Pill key={o} active={f.quien_juega===o} onClick={() => set("quien_juega", o)}>{o}</Pill>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">Edad del gamer</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {EDADES.map(o => (
                    <Pill key={o} active={f.edad_rango===o} onClick={() => set("edad_rango", o)}>{o}</Pill>
                  ))}
                </div>
                {isMenor && (
                  <div data-testid="gaming-menor-notice" className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>Al ser menor de 18, la participación deberá contar con autorización de madre/padre/tutor responsable. Te la vamos a pedir en el último paso.</span>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">¿Dónde jugás? <span className="font-normal text-slate-500">(podés elegir varias)</span></p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {PLATAFORMAS.map(o => (
                    <Pill key={o} active={f.plataformas.includes(o)} onClick={() => toggle("plataformas", o)}>{o}</Pill>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div data-testid="gaming-step-2">
              <StepHeader step={2} total={4} title="Cómo jugás" subtitle="Un poco más sobre tu manera de jugar." />
              <div className="mt-6">
                <Label className="text-slate-900">¿Qué juegos jugás habitualmente?</Label>
                <Input data-testid="gaming-juegos" value={f.juegos} onChange={e => set("juegos", e.target.value)} placeholder="Ej: Valorant, FIFA, LoL..." className="mt-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400" />
              </div>
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">¿Cómo te considerás como jugador?</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Juego de vez en cuando", "Juego bastante seguido", "Juego mucho", "Juego competitivamente"].map(o => (
                    <Pill key={o} active={f.nivel===o} onClick={() => set("nivel", o)}>{o}</Pill>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">¿Con cuántas personas jugás habitualmente online?</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Principalmente solo", "1 a 3", "4 a 10", "Más de 10"].map(o => (
                    <Pill key={o} active={f.cantidad_jugadores===o} onClick={() => set("cantidad_jugadores", o)}>{o}</Pill>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div data-testid="gaming-step-3">
              <StepHeader step={3} total={4} title="Tu comunidad" subtitle="Cómo participás del mundo gamer." />
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">¿Participás actualmente en el mundo gamer de alguna otra manera?</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {ACTIVIDADES.map(o => (
                    <Pill key={o} active={f.actividades.includes(o)} onClick={() => toggle("actividades", o)}>{o}</Pill>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <Label className="text-slate-900">Si pertenecés a un equipo, clan, comunidad, Discord, tenés un canal o hacés streaming, contanos cuál (opcional)</Label>
                <Textarea data-testid="gaming-comunidad" value={f.comunidad_detalle} onChange={e => set("comunidad_detalle", e.target.value)} rows={3} placeholder="Nombre, link al Discord/canal, etc." className="mt-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400" />
              </div>
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">¿Qué te gustaría encontrar en Silicomlan Gaming?</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {INTERESES.map(o => (
                    <Pill key={o} active={f.intereses.includes(o)} onClick={() => toggle("intereses", o)}>{o}</Pill>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div data-testid="gaming-step-4">
              <StepHeader step={4} total={4} title="Tus datos" subtitle="Último paso. Dejanos cómo contactarte." />
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-slate-900">Nombre *</Label>
                  <Input data-testid="gaming-nombre" value={f.nombre} onChange={e => set("nombre", e.target.value)} className="mt-1 border-slate-200 bg-white text-slate-900" />
                </div>
                <div>
                  <Label className="text-slate-900">WhatsApp *</Label>
                  <Input
                    data-testid="gaming-whatsapp"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={f.whatsapp}
                    onChange={e => set("whatsapp", e.target.value)}
                    placeholder="11 1234 5678"
                    className="mt-1 border-slate-200 bg-white text-slate-900"
                  />
                  <p className="mt-1 text-xs text-slate-500">Ingresá un número argentino de 10 dígitos (código de área + número).</p>
                </div>
              </div>
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-900">¿Sos cliente de Silicomlan?</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Sí, soy el titular", "Sí, el servicio está a nombre de otra persona de mi hogar", "No"].map(o => (
                    <Pill key={o} active={f.es_cliente===o} onClick={() => set("es_cliente", o)}>{o}</Pill>
                  ))}
                </div>
                {f.es_cliente === "No" && (
                  <div data-testid="gaming-no-cliente-notice" className="mt-4 flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800">
                    <Gamepad2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span><b>¡Gracias!</b> Podés completar igualmente el relevamiento y participar del sorteo. Recordá que los hogares con Internet de Silicomlan tienen doble chance de ganar.</span>
                  </div>
                )}
              </div>

              {isMenor && (
                <label className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  <Checkbox
                    data-testid="gaming-autoriza-tutor"
                    checked={f.autorizacion_tutor}
                    onCheckedChange={(v) => set("autorizacion_tutor", !!v)}
                    className="mt-0.5 border-amber-500"
                  />
                  <span>Confirmo que la participación cuenta con <b>autorización de madre/padre/tutor responsable</b>.</span>
                </label>
              )}

              <label className="mt-6 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <Checkbox
                  data-testid="gaming-consent"
                  checked={f.consentimiento}
                  onCheckedChange={(v) => set("consentimiento", !!v)}
                  className="mt-0.5 border-slate-400"
                />
                <span>
                  Autorizo a Silicomlan a usar mis datos para la <b className="text-slate-900">participación en el sorteo</b>,
                  <b className="text-slate-900"> contacto relacionado con Silicomlan Gaming</b> y
                  <b className="text-slate-900"> futuras actividades de Silicomlan Gaming</b>.
                  Al enviar acepto las{" "}
                  <a href="/legal/bases" target="_blank" rel="noreferrer" className="text-cyan-600 underline">Bases y Condiciones</a>
                  {" "}y la{" "}
                  <a href="/legal/privacidad" target="_blank" rel="noreferrer" className="text-cyan-600 underline">Política de Privacidad</a>.
                </span>
              </label>
            </div>
          )}

          {/* NAV */}
          <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                data-testid="gaming-prev"
                onClick={prev}
                className="rounded-full border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"
              >
                <ArrowLeft className="mr-1 h-4 w-4" /> Anterior
              </Button>
            ) : <span />}

            {step < 4 ? (
              <Button
                type="button"
                data-testid="gaming-next"
                onClick={next}
                className="rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 px-6 py-5 text-sm font-black uppercase tracking-wider text-white hover:from-sky-600 hover:to-cyan-600"
              >
                Continuar <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                data-testid="gaming-submit"
                disabled={submitting}
                className="rounded-full bg-gradient-to-r from-cyan-500 to-sky-500 px-7 py-6 text-base font-black uppercase tracking-wider text-white shadow-lg shadow-cyan-200 hover:from-cyan-600 hover:to-sky-600"
              >
                {submitting ? "Enviando..." : "Quiero participar"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

const Confirmacion = ({ onReset }) => (
  <section className="min-h-[80vh] bg-gradient-to-br from-cyan-50 via-white to-sky-50 py-20 text-center text-slate-900" data-testid="gaming-confirmation">
    <div className="mx-auto max-w-xl px-6">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-cyan-100">
        <CheckCircle2 className="h-10 w-10 text-cyan-500" />
      </div>
      <h2 className="mt-6 text-4xl font-black md:text-5xl">🎮 ¡Ya estás participando!</h2>
      <p className="mt-5 text-lg text-slate-600">
        Gracias por ayudarnos a conocer la comunidad gamer de Silicomlan. Esto recién empieza.
      </p>
      <div className="mt-10 inline-block rounded-full border border-cyan-200 bg-white px-5 py-2 text-sm font-black uppercase tracking-[0.3em] text-cyan-500 shadow-sm">
        Silicomlan Gaming
      </div>
      <p className="mt-3 text-xs uppercase tracking-widest text-slate-500">La red también se juega.</p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Button
          onClick={onReset}
          data-testid="gaming-back-to-gaming"
          className="rounded-full bg-gradient-to-r from-cyan-500 to-sky-500 px-6 py-5 text-sm font-black uppercase tracking-widest text-white hover:from-cyan-600 hover:to-sky-600"
        >
          Volver a Silicomlan Gaming
        </Button>
        <a
          href="/"
          data-testid="gaming-back-to-silicomlan"
          className="rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
        >
          Volver al sitio
        </a>
      </div>
    </div>
  </section>
);

const Proximamente = () => {
  const items = [
    { icon: Trophy, label: "Torneos" }, { icon: Gamepad2, label: "Competencias" },
    { icon: Users, label: "Comunidad" }, { icon: Sparkles, label: "Gaming & Discord" },
    { icon: Radio, label: "Streaming" }, { icon: Mic2, label: "Creadores" },
    { icon: CalendarDays, label: "Eventos" }, { icon: Gift, label: "Beneficios & Premios" },
  ];
  return (
    <section className="relative overflow-hidden bg-slate-950 py-24 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute left-10 top-20 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-6xl px-6">
        <div className="mb-14 text-center">
          <p className="text-xs font-black uppercase tracking-[0.4em] text-sky-300">Lo que viene</p>
          <h2 className="mt-4 text-4xl font-black leading-tight md:text-6xl">
            <span className="bg-gradient-to-r from-cyan-300 via-white to-sky-300 bg-clip-text text-transparent">
              Esto recién empieza.
            </span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-300 md:text-lg">
            Estamos construyendo Silicomlan Gaming junto a nuestra comunidad.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ icon: Icon, label }) => (
            <div key={label} className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-cyan-300/40">
              <Icon className="h-6 w-6 text-sky-300" />
              <p className="mt-3 font-bold uppercase tracking-widest">{label}</p>
              <span className="absolute right-3 top-3 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-cyan-200">
                Próximamente
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Gaming = () => {
  const [sent, setSent] = useState(() => sessionStorage.getItem(SUBMIT_KEY) === "1");

  useEffect(() => {
    parseAndStoreUtms();
    track("page_view");
    document.title = "Silicomlan Gaming · La red también se juega";
  }, []);

  const scrollToEncuesta = () => {
    track("click_participar");
    document.querySelector("#encuesta")?.scrollIntoView({ behavior: "smooth" });
  };

  const reset = () => {
    sessionStorage.removeItem(SUBMIT_KEY);
    setSent(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Hero onCta={scrollToEncuesta} />
      <Beneficios />
      <div aria-hidden="true" className="h-24 bg-gradient-to-b from-slate-950 to-white" />
      <Posicionamiento />
      {sent ? <Confirmacion onReset={reset} /> : <Encuesta onSuccess={() => setSent(true)} />}
      <Proximamente />
      <footer className="border-t border-slate-100 bg-white py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Silicomlan Gaming ·{" "}
        <a href="/" data-testid="gaming-footer-home" className="font-semibold text-cyan-500 hover:text-cyan-600">Volver al sitio</a>
        {" · "}
        <a href="/legal/bases" className="hover:text-slate-700">Bases y Condiciones</a>
        {" · "}
        <a href="/legal/privacidad" className="hover:text-slate-700">Privacidad</a>
      </footer>
    </div>
  );
};

export default Gaming;
